import { useCallback } from 'react';
import Image from 'components/medias/Image';
import adminMode from 'assets/images/illustrations/admin_mode.png';
import { type Theme, Button, Fade, ListItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { COLORS } from 'theme/MainTheme';
import { FormattedMessage } from 'react-intl';
import { useSelectedLinkContext, useAdminModeContext, useTourContext } from 'contexts';
import { navigateToSelectedChannelRoute } from 'utils/history';
import Route from 'routes/Routes';
import { StateSetter } from 'types/utils/React';
import { trackEvent, GACommonEventAction, GAEventCategory } from 'services/googleAnalytics';
import { TOUR_STEPS } from '../../App/OnBoarding/OnBoardingTour';
import clsx from 'clsx';
import { linkHasAdminAccess } from 'utils/link';

const useStyles = makeStyles((theme: Theme) => ({
  changeModeContainer: {

  },
  changeModeSubContainer: {
    position: 'relative',
    textAlign: 'center',
  },
  changeModeImage: {
    width: '100%',

    // [theme.breakpoints.down('md')]: {
    //   width: '40%',
    // },

    [theme.breakpoints.down('md')]: {
      width: '60%',
    },
  },
  changeModeButtonContainer: {
    position: 'absolute',
    
    [theme.breakpoints.down(1400)]: {
      top: '79%',
      left: '3%',
      width: '86%',
    },

    [theme.breakpoints.up('md')]: {
      top: '77%',
      left: '8%',
      width: '75%',
    },

    [theme.breakpoints.down('md')]: {
      left: '50%',
      top: '80%',
      transform: 'translate(-54%, 0)',
    },
  },
  changeModeButton: {
    backgroundColor: COLORS.BLUE,
    color: COLORS.WHITE,
    border: 'none',
    fontSize: 13,
    padding: '1em 5px',
    textTransform: 'none',
    width: '100%',
    borderRadius: 10,
    '&:hover': {
      backgroundColor: COLORS.BLUE,
    },
    [theme.breakpoints.down(1750)]: {
      padding: '.7em 5px',
    },
    [theme.breakpoints.down(1500)]: {
      padding: '.5em 2px',
    },
  },
  selected: {
    border: `2px solid ${COLORS.BLUE}`,
  },
  leaveAdminModeButtonContainer: {
    // [theme.breakpoints.down(1749)]: {
    //   top: '79%',
    //   left: '3%',
    //   width: '86%',
    // },
  }
}), {name: 'ChangeModeButton'});

type ChangeModeButtonProps = {
  setIsMenuOpened?: StateSetter<boolean>;
};
const ChangeModeButton = ({
  setIsMenuOpened,
}: ChangeModeButtonProps): JSX.Element | null => {
  const classes = useStyles();
  const { currentStep, isTourOpen } = useTourContext();
  const { selectedLink } = useSelectedLinkContext();
  const { isAdminMode, setIsAdminMode } = useAdminModeContext();
  const show =
    (isTourOpen && currentStep >= TOUR_STEPS.ADMIN) ||
    (!isTourOpen && linkHasAdminAccess(selectedLink));

  const onClick = useCallback(() => {
    trackEvent(
      GAEventCategory.BUTTON,
      GACommonEventAction.CLICK,
      'Mode Administrateur',
    );

    if (setIsMenuOpened !== undefined) {
      setIsMenuOpened(false);
    }

    setIsAdminMode(!isAdminMode);

    if (isAdminMode) {
      navigateToSelectedChannelRoute(selectedLink, Route.HOME);
    } else {
      navigateToSelectedChannelRoute(selectedLink, Route.ADMIN);
    }
  }, [isAdminMode, selectedLink, setIsAdminMode, setIsMenuOpened]);

  const buttonLabelId = isAdminMode
    ? 'button.exitAdminMode'
    : 'button.adminMode';
  
  const selectedStepClassName = isTourOpen && currentStep === TOUR_STEPS.ADMIN
    ? classes.selected
    : undefined
  
  return (
    <ListItem style={{justifyContent: 'center'}}>
      <Fade in={show}>
        <div className={classes.changeModeContainer}>
          <div
            className={clsx([
              classes.changeModeSubContainer,
              'tour-step-admin',
            ])}
          >
            <Image
              alt="Admin mode"
              className={clsx([classes.changeModeImage, selectedStepClassName])}
              src={adminMode}
            />

            <div className={clsx(
              classes.changeModeButtonContainer,
              {[classes.leaveAdminModeButtonContainer]: isAdminMode}
            )}>
              <Button
                className={classes.changeModeButton}
                variant="contained"
                onClick={onClick}
              >
                <FormattedMessage id={buttonLabelId} />
              </Button>
            </div>
          </div>
        </div>
      </Fade>
    </ListItem>
  );
};

export default ChangeModeButton;
